/**
 * LabInvestigationsPage (Phase-2, Module 6 — lab admin side)
 *
 * Lab admin views assigned investigations, marks them in-progress, uploads the
 * report file (PDF/image), and completes them.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Biotech as InvIcon, Upload as UploadIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, FileUpload } from '../../components/common';
import { InvestigationApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import { TEST_TYPES, labelFor } from '../../utils/phase2Constants';
import type { InvestigationRequest } from '../../types/phase2';

const LabInvestigationsPage: React.FC = () => {
  const [rows, setRows] = useState<InvestigationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('ASSIGNED');
  const [uploadId, setUploadId] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [results, setResults] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: Record<string, unknown> = { page: 0, size: 100 };
      if (statusFilter) params.status = statusFilter;
      const page = await InvestigationApi.listAll(params);
      setRows(page?.content || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load investigations'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<InvestigationRequest>, label: string) => {
    try { await fn(); load(); } catch (e: any) { setError(e?.response?.data?.message || `${label} failed`); }
  };

  const handleUpload = async () => {
    if (uploadId == null || !file) return;
    setUploading(true); setError(null);
    try { await InvestigationApi.uploadReport(uploadId, file, results || undefined); setUploadId(null); setFile(null); setResults(''); load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const columns: GridColDef[] = [
    { field: 'investigationNumber', headerName: 'Inv. No.', width: 140 },
    { field: 'officerName', headerName: 'Officer', width: 150, renderCell: (p) => p.value || '—' },
    { field: 'testType', headerName: 'Test Type', width: 140, renderCell: (p) => <Chip size="small" label={labelFor(TEST_TYPES, p.value)} variant="outlined" /> },
    { field: 'testName', headerName: 'Test', flex: 1.1, minWidth: 140, renderCell: (p) => p.value || p.row.customTestName || '—' },
    { field: 'scheduledDate', headerName: 'Scheduled', width: 120 },
    { field: 'status', headerName: 'Status', width: 140, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Actions', width: 320, sortable: false,
      renderCell: (p) => {
        const s = p.row.status as string;
        const id = p.row.id as number;
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            {(s === 'ASSIGNED' || s === 'SAMPLE_COLLECTED') && <Button size="small" variant="contained" color="warning" onClick={() => act(() => InvestigationApi.markInProgress(id), 'In Progress')} sx={{ textTransform: 'none', mr: 0.5 }}>In Progress</Button>}
            {(s === 'SAMPLE_COLLECTED' || s === 'IN_PROGRESS') && <Button size="small" variant="contained" color="primary" startIcon={<UploadIcon />} onClick={() => setUploadId(id)} sx={{ textTransform: 'none', mr: 0.5 }}>Upload Report</Button>}
            {s === 'REPORT_READY' && <Button size="small" variant="contained" color="success" onClick={() => act(() => InvestigationApi.complete(id), 'Complete')} sx={{ textTransform: 'none', mr: 0.5 }}>Complete</Button>}
            {p.row.reportUrl && <Button size="small" href={toAbsoluteFileUrl(p.row.reportUrl)} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>View</Button>}
          </Box>
        );
      },
    },
  ];

  return (
    <PageScaffold title="Lab Investigations" subtitle="Process assigned investigations & upload reports" icon={<InvIcon />}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="ASSIGNED">Assigned</MenuItem>
          <MenuItem value="SAMPLE_COLLECTED">Sample Collected</MenuItem>
          <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
          <MenuItem value="REPORT_READY">Report Ready</MenuItem>
          <MenuItem value="COMPLETED">Completed</MenuItem>
        </Select>
      </FormControl>
      <Box sx={{ height: 540, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>

      <Dialog open={uploadId != null} onClose={() => !uploading && setUploadId(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Investigation Report</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Results / Findings (optional)" multiline minRows={2} value={results} onChange={(e) => setResults(e.target.value)} fullWidth />
            <FileUpload onFileSelected={setFile} uploading={uploading} label="Select report file (PDF / image)" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadId(null)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!file || uploading} sx={{ textTransform: 'none', fontWeight: 600 }}>{uploading ? 'Uploading...' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>
    </PageScaffold>
  );
};

export default LabInvestigationsPage;